import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { getUserProfile } from '../redux/auth/authSlice';
import tokenManager from '../utils/tokenManager';
import { logger } from '../utils/logger';

// App.tsx から移設（ロジックは変更していない）。テストから単体で描画できるようにするため。


// 管理者権限が必要なルートを保護するためのコンポーネント
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { token, isAuthenticated, user, isLoading } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // ページロード時に認証状態を確認
    const checkAuthentication = async () => {
      try {
        // トークンマネージャーを使用してトークンを取得
        let accessToken = token || tokenManager.getToken();
        let currentRefreshToken = tokenManager.getRefreshToken();

        logger.log('AdminRoute Auth check: tokens cross-checked', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!currentRefreshToken
        });

        // リフレッシュトークンがあるがアクセストークンがない場合はリフレッシュを試みる
        if (currentRefreshToken && !accessToken) {
          logger.log('AdminRoute Auth check: Has refresh token but no access token - attempting refresh');
          try {
            const authService = (await import('../services/api/authService')).default;
            const refreshResult = await authService.refreshToken();
            if (refreshResult && refreshResult.token) {
              logger.log('AdminRoute Auth check: Successfully refreshed token');
              accessToken = refreshResult.token; // 新しいアクセストークンで更新
            }
          } catch (refreshError) {
            console.error('AdminRoute Auth check: Failed to refresh token:', refreshError);
          }
        }

        if (accessToken) {
          // トークンがあるが、ユーザー情報がない場合は取得を試みる
          if (!user) {
            await dispatch(getUserProfile()).unwrap();
          }
        }
      } catch (error) {
        // ユーザープロファイル取得に失敗した場合
        console.error('Authentication check failed for admin route:', error);

        // リフレッシュトークンが存在するか確認
        const currentRefreshToken = tokenManager.getRefreshToken();
        logger.log('AdminRoute - authentication check:', {
          hasRefreshToken: !!currentRefreshToken,
          error: error
        });

        if (!currentRefreshToken) {
          // リフレッシュトークンがない場合のみトークンを削除
          tokenManager.clearTokens(false);
        } else {
          // リフレッシュトークンでトークンの再取得を試みる
          try {
            const authService = (await import('../services/api/authService')).default;
            await authService.refreshToken();
            logger.log('AdminRoute Auth check: Attempted token refresh after profile fetch failure');
          } catch (refreshError) {
            console.error('AdminRoute Auth check: Failed to refresh token after profile fetch failure:', refreshError);
          }
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };

    // 認証チェックを実行
    checkAuthentication();

    // 安全策として最大5秒後には強制的にローディング状態を解除
    const timeout = setTimeout(() => {
      console.warn('Admin auth check timeout reached - forcing state update');
      setIsCheckingAuth(false);

      if (token && !user) {
        // リフレッシュトークンを確認
        const currentRefreshToken = tokenManager.getRefreshToken();
        logger.log('Admin auth check timeout:', { hasRefreshToken: !!currentRefreshToken });

        if (!currentRefreshToken) {
          console.warn('Admin auth check timeout - no refresh token, forcing logout');
          tokenManager.clearTokens(false);
          window.location.href = '/admin/login';
        } else {
          logger.log('Admin auth check timeout - refresh token exists, maintaining state');
        }
      }
    }, 5000);

    // クリーンアップでは、この実行で張ったタイマーを直接解除する。
    // state に入れたタイマーを参照すると初回描画時の null を掴んだままになり、
    // user 取得後も user=null を掴んだ古いタイマーが発火して管理者を /admin/login へ追い出していた。
    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch, token, user]);

  // 認証チェック中またはロード中は何も表示しない
  if (isCheckingAuth || isLoading) {
    return <div>Loading...</div>;
  }

  // 認証チェック - トークンマネージャーからリフレッシュトークンを取得
  const refreshToken = tokenManager.getRefreshToken();
  logger.log('AdminRoute 認証チェック:', {
    hasToken: !!token,
    hasUser: !!user,
    hasRefreshToken: !!refreshToken,
    isAdmin: user?.role === 'admin'
  });

  // 判定ロジック修正 - tokenManagerから直接状態を確認
  // ケース1: ユーザー情報があり、管理者権限を持っている場合はアクセス許可
  if (user) {
    // 管理者権限チェック
    const isAdmin = user.role === 'admin';

    if (isAdmin) {
      logger.log('AdminRoute: User is admin, access granted');
      return <>{children}</>;
    } else {
      logger.log('AdminRoute: User is not admin, redirecting to home');
      return <Navigate to="/" replace />;
    }
  }

  // ケース2: トークンがある場合はローディング表示（ユーザー情報取得待ち）
  if (token || tokenManager.hasValidToken()) {
    logger.log('AdminRoute: Token exists but no user info yet, showing loading');
    return <div>管理者情報取得中...</div>;
  }

  // ケース3: リフレッシュトークンがある場合もローディング表示（トークンリフレッシュ待ち）
  if (tokenManager.hasRefreshToken()) {
    logger.log('AdminRoute: Refresh token exists, waiting for token refresh');
    return <div>認証情報更新中...</div>;
  }

  // 万が一のフォールバック
  logger.log('AdminRoute: Fallback case, redirecting to login');
  return <Navigate to="/admin/login" replace />;
};

export default AdminRoute;
